import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const S = {
  btn: (v) => ({
    padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
    background: v === 'danger' ? '#ef4444' : v === 'success' ? '#22c55e' : v === 'primary' ? '#3b82f6' : '#2a2a4a',
    color: '#fff',
  }),
  card: {
    background: '#0d1117', borderRadius: 10, border: '1px solid #2a2a4a', padding: 20, marginBottom: 16,
  },
};

export default function ApiKeyManager() {
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('Proxy API Key');
  const [newKeyExpiry, setNewKeyExpiry] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const token = sessionStorage.getItem('token');
  const API_BASE = window.__ENV__?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || '';

  const apiFetch = useCallback(async (path, opts = {}) => {
    const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(`${API_BASE}/api/api-keys${path}`, { ...opts, headers });
  }, [token, API_BASE]);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/');
      if (res.ok) {
        const data = await res.json();
        setKeys(data.keys || []);
      }
    } catch (e) { console.error('Load keys error:', e); }
    setLoading(false);
  }, [apiFetch]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const createKey = async () => {
    try {
      const body = { name: newKeyName || 'Proxy API Key' };
      if (newKeyExpiry) body.expires_days = parseInt(newKeyExpiry);
      const res = await apiFetch('/', { method: 'POST', body: JSON.stringify(body) });
      if (res.ok) {
        const data = await res.json();
        setCreatedKey(data);
        setNewKeyName('Proxy API Key');
        setNewKeyExpiry('');
        loadKeys();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to create key');
      }
    } catch (e) { alert('Failed to create key: ' + e.message); }
  };

  const deleteKey = async (id) => {
    if (!window.confirm('Revoke this API key? Any proxy connections using it will stop working.')) return;
    await apiFetch(`/${id}`, { method: 'DELETE' });
    loadKeys();
  };

  const toggleKey = async (id) => {
    await apiFetch(`/${id}/toggle`, { method: 'PATCH' });
    loadKeys();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const timeAgo = (date) => {
    if (!date) return 'Never';
    const diff = Date.now() - new Date(date).getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, color: '#e2e8f0' }}>API Keys</h3>
          <p style={{ margin: '4px 0 0', fontSize: 11, color: '#8888aa' }}>
            Use API keys for proxy authentication. Configure your browser proxy with username:api-key as credentials.
          </p>
        </div>
      </div>

      {/* Created key banner — shown once */}
      {createdKey && (
        <div style={{
          background: '#22c55e15', border: '1px solid #22c55e30', borderRadius: 8,
          padding: 12, marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#22c55e', marginBottom: 8 }}>
            API Key Created — Copy it now! It won't be shown again.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              readOnly
              value={createdKey.key}
              style={{
                flex: 1, padding: '8px 12px', background: '#0d1117', color: '#22c55e',
                border: '1px solid #22c55e50', borderRadius: 6, fontFamily: 'monospace', fontSize: 13,
              }}
              onClick={e => e.target.select()}
            />
            <button
              style={{ ...S.btn('success'), padding: '8px 16px' }}
              onClick={() => copyToClipboard(createdKey.key)}
            >{copied ? 'Copied!' : 'Copy'}</button>
            <button
              style={{ ...S.btn(), padding: '8px 12px' }}
              onClick={() => setCreatedKey(null)}
            >Dismiss</button>
          </div>
          <div style={{ fontSize: 10, color: '#8888aa', marginTop: 8 }}>
            <strong>Proxy credentials:</strong> Username: <code style={{ color: '#f59e0b' }}>{user?.username || 'your-username'}</code> &nbsp;|&nbsp;
            Password: <code style={{ color: '#f59e0b' }}>{createdKey.key}</code>
          </div>
        </div>
      )}

      {/* Create new key */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 10, color: '#8888aa', display: 'block', marginBottom: 4 }}>Key Name</label>
          <input
            type="text"
            value={newKeyName}
            onChange={e => setNewKeyName(e.target.value)}
            placeholder="e.g., Work Laptop, Home Desktop"
            style={{
              width: '100%', padding: '6px 10px', background: '#16213e', color: '#e2e8f0',
              border: '1px solid #2a2a4a', borderRadius: 6, fontSize: 12, outline: 'none',
            }}
          />
        </div>
        <div style={{ width: 120 }}>
          <label style={{ fontSize: 10, color: '#8888aa', display: 'block', marginBottom: 4 }}>Expires (days)</label>
          <input
            type="number"
            value={newKeyExpiry}
            onChange={e => setNewKeyExpiry(e.target.value)}
            placeholder="Never"
            min="1"
            style={{
              width: '100%', padding: '6px 10px', background: '#16213e', color: '#e2e8f0',
              border: '1px solid #2a2a4a', borderRadius: 6, fontSize: 12, outline: 'none',
            }}
          />
        </div>
        <button style={S.btn('primary')} onClick={createKey}>
          Generate Key
        </button>
      </div>

      {/* Keys list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 20, color: '#8888aa', fontSize: 12 }}>Loading...</div>
      ) : keys.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 20, color: '#8888aa', fontSize: 12 }}>
          No API keys yet. Generate one to use with the SaaS proxy.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2a2a4a' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: '#8888aa', fontSize: 10 }}>Key</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: '#8888aa', fontSize: 10 }}>Name</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: '#8888aa', fontSize: 10 }}>Status</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: '#8888aa', fontSize: 10 }}>Last Used</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: '#8888aa', fontSize: 10 }}>Expires</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: '#8888aa', fontSize: 10 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k.id} style={{ borderBottom: '1px solid #1a1a2a', opacity: k.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: '#3b82f6' }}>
                  {k.key_prefix}...
                </td>
                <td style={{ padding: '6px 8px', color: '#e2e8f0' }}>{k.name}</td>
                <td style={{ padding: '6px 8px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                    background: k.is_active ? '#22c55e20' : '#ef444420',
                    color: k.is_active ? '#22c55e' : '#ef4444',
                  }}>
                    {k.is_active ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td style={{ padding: '6px 8px', color: '#8888aa', fontSize: 11 }}>
                  {k.last_used_at ? (
                    <span title={new Date(k.last_used_at).toLocaleString()}>
                      {timeAgo(k.last_used_at)}
                      {k.last_used_ip && <span style={{ color: '#555', marginLeft: 4 }}>({k.last_used_ip})</span>}
                    </span>
                  ) : 'Never'}
                </td>
                <td style={{ padding: '6px 8px', color: '#8888aa', fontSize: 11 }}>
                  {k.expires_at ? new Date(k.expires_at).toLocaleDateString() : 'Never'}
                </td>
                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                  <button
                    style={{ ...S.btn(), padding: '3px 8px', fontSize: 10, marginRight: 4 }}
                    onClick={() => toggleKey(k.id)}
                  >{k.is_active ? 'Disable' : 'Enable'}</button>
                  <button
                    style={{ ...S.btn('danger'), padding: '3px 8px', fontSize: 10 }}
                    onClick={() => deleteKey(k.id)}
                  >Revoke</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
